/**
 * Phase 1.2 Bloc C — Hook useAICredits
 *
 * Fetches the number of AI credits consumed this calendar month from `ai_usage`
 * and resolves the plan limit from the user's current tier.
 *
 * RLS on `ai_usage` filters by user_id automatically — no explicit filter needed.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useUserTier } from './useUserTier';
import { getTierLimits } from '../lib/capabilities';

export interface AICreditsState {
  /** Credits consumed this calendar month. */
  used: number;
  /** Plan limit. Infinity when the plan is truly unlimited (-1 in DB convention). */
  limit: number;
  /** Remaining credits (may be Infinity for unlimited plans). */
  remaining: number;
  /** True during the initial fetch. */
  loading: boolean;
  /** Re-fetch credits from DB (call after an AI action succeeds). */
  refetch: () => void;
}

export function useAICredits(): AICreditsState {
  const tier = useUserTier();
  const [refreshKey, setRefreshKey] = useState(0);

  const [state, setState] = useState<Omit<AICreditsState, 'refetch'>>({
    used: 0,
    limit: 20,
    remaining: 20,
    loading: true,
  });

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    const rawLimit = getTierLimits(tier).aiCreditsPerMonth;
    // Existing convention: -1 means unlimited
    const limit = rawLimit === -1 ? Infinity : rawLimit;

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    supabase
      .from('ai_usage')
      .select('credits_consumed')
      .gte('created_at', startOfMonth.toISOString())
      .then(({ data, error }) => {
        if (error) {
          console.warn('[useAICredits] Failed to fetch AI usage:', error);
          setState({ used: 0, limit, remaining: limit, loading: false });
          return;
        }

        const used =
          data?.reduce(
            (sum: number, row: { credits_consumed: number }) => sum + (row.credits_consumed ?? 0),
            0
          ) ?? 0;

        const remaining = limit === Infinity ? Infinity : Math.max(0, limit - used);
        setState({ used, limit, remaining, loading: false });
      });
  }, [tier, refreshKey]);

  return { ...state, refetch };
}
